<template>
    <div>
        <v-dialog
            v-model="addBillingCodeDialog"
            persistent
            width="620"
        >
            <template v-slot:activator="{ attrs }">
                <v-btn
                    depressed
                    outlined
                    v-bind="attrs"
                    @click="openDialog"
                >
                    <v-icon
                        left
                        class="action-icon"
                    >
                        mdi-plus
                    </v-icon>
                    {{ allocationMode && billingCodes.length
                        ? 'Edit Billing Codes'
                        : 'Add Billing Code' }}
                </v-btn>
            </template>
            <v-card>
                <v-card-title class="text-h5 lighten-2">
                    <span>{{ allocationMode ? 'Allocate Billing Codes' : 'Add Billing Code' }}</span>
                </v-card-title>
                <v-divider />
                <v-card-text>
                    <div class="billing-code-prompt text-body-2 text--secondary mt-4">
                        <template v-if="allocationMode">
                            Allocate the {{ formattedAllocationBudget }} worked since arrival
                            across the selected billing codes.
                        </template>
                        <template v-else>
                            Choose a billing code and adjust the work time if necessary.
                        </template>
                    </div>
                    <v-form
                        ref="form"
                        v-model="formValid"
                    >
                        <template v-if="allocationMode">
                            <v-autocomplete
                                v-model="selectedAllocationCodes"
                                :search-input.sync="billingCodeSearch"
                                :items="companyBillingCodes.map((item) => item.code)"
                                label="Billing Codes"
                                multiple
                                chips
                                small-chips
                                deletable-chips
                                hide-selected
                                auto-select-first
                                outlined
                                dense
                                class="upper-case mt-5"
                                :rules="[value => value.length > 0
                                    || 'At least one billing code is required']"
                                @change="syncAllocationDraft"
                                @keydown.native.capture="handleBillingCodeKeydown"
                            />
                            <v-alert
                                v-if="selectedAllocationCodes.length > allocationBudgetMinutes"
                                dense
                                outlined
                                type="warning"
                            >
                                There must be at least one minute available for each code.
                            </v-alert>
                            <div
                                v-if="allocationDraft.length > 0 && canAllocateDraft"
                                class="segmented-allocation"
                            >
                                <div class="segmented-allocation__track">
                                    <div
                                        v-for="(item, index) in allocationDraft"
                                        :key="item.code"
                                        class="segmented-allocation__segment"
                                        :style="{
                                            backgroundColor: allocationColor(index),
                                            width: allocationWidth(item.minutes),
                                        }"
                                        :title="`${item.code}: ${formatMinutes(item.minutes)}`"
                                    />
                                    <input
                                        v-for="(item, index) in allocationDraft.slice(0, -1)"
                                        :key="`boundary-${item.code}`"
                                        class="segmented-allocation__boundary"
                                        type="range"
                                        step="1"
                                        min="1"
                                        :max="allocationBudgetMinutes - 1"
                                        :value="boundaryValue(index)"
                                        :aria-label="`Adjust time between ${item.code} and ${allocationDraft[index + 1].code}`"
                                        @input="setBoundary(index, Number($event.target.value))"
                                    >
                                </div>
                                <div class="segmented-allocation__legend">
                                    <div
                                        v-for="(item, index) in allocationDraft"
                                        :key="`legend-${item.code}`"
                                        class="segmented-allocation__legend-item"
                                    >
                                        <span
                                            class="segmented-allocation__swatch"
                                            :style="{ backgroundColor: allocationColor(index) }"
                                        />
                                        <strong>{{ item.code }}</strong>
                                        <span>{{ formatMinutes(item.minutes) }}</span>
                                    </div>
                                </div>
                                <div class="text-caption text--secondary mt-3">
                                    Drag the dividers to allocate the full
                                    {{ formattedAllocationBudget }}.
                                </div>
                            </div>
                        </template>
                        <v-row v-else class="mt-5">
                            <v-col
                                cols="12"
                                sm="12"
                                class="py-0"
                            >
                                <v-select
                                    v-if="addBillingCodeDialog"
                                    v-model="code"
                                    label="Billing Code"
                                    :items="availableBillingCodes.map((item) => item.code)"
                                    outlined
                                    dense
                                    class="upper-case"
                                    :rules="billingCodeRules"
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                md="12"
                                class="py-0"
                            >
                                <v-select
                                    v-if="addBillingCodeDialog"
                                    v-model="minutes"
                                    :items="billingCodeTimeWorked"
                                    item-text="label"
                                    item-value="value"
                                    label="Time Worked"
                                    outlined
                                    dense
                                    class="upper-case"
                                    :rules="minutesRules"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <v-btn
                        style="text-transform: none !important;"
                        outlined
                        @click="closedDialog"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        style="text-transform: none !important"
                        depressed
                        color="primary"
                        :disabled="!canAddBillingCode"
                        @click="save"
                    >
                        <span>{{ allocationMode ? 'Apply' : 'Add' }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-card v-if="billingCodes.length > 0">
            <v-card-title class="text-h5 lighten-2">
                <span>Billing Codes</span>
            </v-card-title>
            <v-card-subtitle v-if="allocationMode" class="pb-2">
                {{ formattedAllocatedMinutes }} allocated ·
                {{ formattedRemainingMinutes }} remaining
            </v-card-subtitle>
            <v-divider />
            <v-data-table
                :headers="headers"
                :items="billingCodes"
                :items-per-page="5"
                class="billing-code-table elevation-1"
                hide-default-footer
                dense
            >
                <template v-slot:[`item.code`]="{ item }">
                    {{ item.code }}
                </template>
                <template v-slot:[`item.minutes`]="{ item }">
                    {{ allocationMode
                        ? formatMinutes(item.minutes)
                        : getBillingMinutesLabel(item.minutes) }}
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <v-icon
                        class="action-icon"
                        @click="deleteBillingCode(item)"
                    >
                        delete
                    </v-icon>
                </template>
            </v-data-table>
            <v-alert
                v-if="allocationMode && !allocationValid"
                dense
                outlined
                type="warning"
                class="ma-4"
            >
                Allocate the remaining {{ formattedRemainingMinutes }} before responding.
            </v-alert>
        </v-card>
    </div>
</template>
<script>
import CompanyBillingMixin from '@/mixins/CompanyBillingMixin';

// Keep billing-code colors distinct from the job timeline palette. These colors
// describe allocation segments only and must not imply a relationship to jobs.
const BILLING_CODE_ALLOCATION_COLORS = [
    '#CDDC39',
    '#00BCD4',
    '#FF7043',
    '#78909C',
    '#9CCC65',
    '#FFCA28',
];

export default {
    mixins: [
        CompanyBillingMixin,
    ],
    props: {
        billingCodes: {
            type: Array,
            default: () => [],
        },
        allocationMinutes: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            headers: [
                { text: 'Code', value: 'code' },
                { text: 'Time Worked', value: 'minutes' },
                {
                    text: 'Delete',
                    align: 'center',
                    value: 'delete',
                    sortable: false,
                }
            ],
            formValid: false,
            addBillingCodeDialog: false,
            editBillingCodeDialog: false,
            code: '',
            minutes: '',
            selectedAllocationCodes: [],
            allocationDraft: [],
            billingCodeSearch: null,
        };
    },
    computed: {
        allocationMode() {
            return Number.isFinite(this.allocationMinutes);
        },
        allocationBudgetMinutes() {
            return this.allocationMode
                ? Math.max(1, Math.round(this.allocationMinutes))
                : null;
        },
        allocatedMinutes() {
            return this.billingCodes.reduce(
                (total, billingCode) => total + (Number(billingCode.minutes) || 0),
                0
            );
        },
        remainingMinutes() {
            if(!this.allocationMode) {
                return null;
            }
            return Math.max(0, this.allocationBudgetMinutes - this.allocatedMinutes);
        },
        allocationValid() {
            return !this.allocationMode || (
                this.billingCodes.length > 0
                && this.allocatedMinutes === this.allocationBudgetMinutes
            );
        },
        availableBillingCodes() {
            const selectedCodes = new Set(this.billingCodes.map(item => item.code));
            return this.companyBillingCodes.filter(item => !selectedCodes.has(item.code));
        },
        canAddBillingCode() {
            if(this.allocationMode) {
                return this.addBillingCodeDialog
                    && this.canAllocateDraft
                    && this.allocationDraft.length > 0
                    && this.draftAllocatedMinutes === this.allocationBudgetMinutes;
            }
            return this.addBillingCodeDialog
                && Boolean(this.code)
                && Number(this.minutes) > 0
                && this.formValid;
        },
        canAllocateDraft() {
            return this.selectedAllocationCodes.length <= this.allocationBudgetMinutes;
        },
        draftAllocatedMinutes() {
            return this.allocationDraft.reduce(
                (total, item) => total + (Number(item.minutes) || 0),
                0
            );
        },
        formattedAllocationBudget() {
            return this.formatMinutes(this.allocationBudgetMinutes);
        },
        formattedAllocatedMinutes() {
            return this.formatMinutes(this.allocatedMinutes);
        },
        formattedRemainingMinutes() {
            return this.formatMinutes(this.remainingMinutes);
        },
    },
    watch: {
        allocationValid: {
            immediate: true,
            handler(valid) {
                this.$emit('allocation-valid', valid);
            },
        },
    },
    methods: {
        openDialog(){
            this.addBillingCodeDialog = true;
            this.resetFormData();
        },
        closedDialog() {
            // this.resetFormData();
            this.addBillingCodeDialog = false;
        },
        capitalized(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
        resetFormData() {
            const onlyBillingCode = this.companyBillingCodes.length === 1
                ? this.companyBillingCodes[0]
                : null;
            if(this.allocationMode) {
                this.billingCodeSearch = null;
                this.selectedAllocationCodes = this.billingCodes.map(item => item.code);
                this.allocationDraft = this.billingCodes.map(item => ({
                    code: item.code,
                    minutes: Number(item.minutes) || 0,
                }));
                if(this.selectedAllocationCodes.length === 0 && onlyBillingCode) {
                    this.selectedAllocationCodes = [onlyBillingCode.code];
                    this.distributeAllocationEvenly();
                } else if(this.allocationDraft.length > 0
                    && this.allocationDraft.reduce((total, item) => (
                        total + item.minutes
                    ), 0) !== this.allocationBudgetMinutes) {
                    this.distributeAllocationEvenly();
                }
                this.formValid = false;
                return;
            }
            const onlyTimeWorked = this.billingCodeTimeWorked.length === 1
                ? this.billingCodeTimeWorked[0]
                : null;
            this.code = onlyBillingCode?.code || '';
            this.minutes = onlyTimeWorked?.value ?? '';
            this.formValid = false;
        },
        async save() {
            if(this.allocationMode) {
                this.billingCodes.splice(0, this.billingCodes.length,
                    ...this.allocationDraft.map(item => ({
                        code: item.code,
                        quantity: 1,
                        minutes: Number(item.minutes),
                    }))
                );
                await this.handleBillingCodes();
                this.addBillingCodeDialog = false;
                return;
            }
            this.billingCodes.push({
                code: this.code,
                quantity: 1,
                minutes: Number(this.minutes),
            });

            this.handleBillingCodes();

            this.resetFormData();
            this.addBillingCodeDialog = false;
        },
        async getBillingAmount(code) {
            let billingCode = await this.companyBillingCodes.find(item => item.code === code);
            return billingCode.amount;
        },
        async deleteBillingCode(item) {
            let index = this.billingCodes.indexOf(item);
            this.billingCodes.splice(index, 1);
            this.handleBillingCodes();
        },
        syncAllocationDraft() {
            this.allocationDraft = this.selectedAllocationCodes.map(code => ({
                code,
                minutes: 0,
            }));
            if(this.canAllocateDraft) {
                this.distributeAllocationEvenly();
            }
        },
        handleBillingCodeKeydown(event) {
            if(event.key !== 'Tab' || !this.billingCodeSearch?.trim()) {
                return;
            }
            const search = this.billingCodeSearch.trim().toLowerCase();
            const availableCodes = this.companyBillingCodes
                .map(item => item.code)
                .filter(code => !this.selectedAllocationCodes.includes(code));
            const matchingCode = availableCodes.find(code => (
                code.toLowerCase() === search
            )) || availableCodes.find(code => (
                code.toLowerCase().startsWith(search)
            )) || availableCodes.find(code => (
                code.toLowerCase().includes(search)
            ));
            if(!matchingCode) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            this.selectedAllocationCodes.push(matchingCode);
            this.syncAllocationDraft();
            this.billingCodeSearch = null;
        },
        distributeAllocationEvenly() {
            const count = this.allocationDraft.length;
            if(count === 0 || !this.canAllocateDraft) {
                return;
            }
            const baseMinutes = Math.floor(this.allocationBudgetMinutes / count);
            let remainder = this.allocationBudgetMinutes % count;
            this.allocationDraft.forEach(item => {
                item.minutes = baseMinutes + (remainder > 0 ? 1 : 0);
                remainder -= remainder > 0 ? 1 : 0;
            });
        },
        boundaryValue(index) {
            return this.allocationDraft
                .slice(0, index + 1)
                .reduce((total, item) => total + item.minutes, 0);
        },
        boundaryMin(index) {
            return index === 0 ? 1 : this.boundaryValue(index - 1) + 1;
        },
        boundaryMax(index) {
            return index === this.allocationDraft.length - 2
                ? this.allocationBudgetMinutes - 1
                : this.boundaryValue(index + 1) - 1;
        },
        setBoundary(index, value) {
            const currentBoundary = this.boundaryValue(index);
            const nextBoundary = Math.min(
                this.boundaryMax(index),
                Math.max(this.boundaryMin(index), value)
            );
            const difference = nextBoundary - currentBoundary;
            this.allocationDraft[index].minutes += difference;
            this.allocationDraft[index + 1].minutes -= difference;
        },
        allocationWidth(minutes) {
            return `${(minutes / this.allocationBudgetMinutes) * 100}%`;
        },
        allocationColor(index) {
            return BILLING_CODE_ALLOCATION_COLORS[
                index % BILLING_CODE_ALLOCATION_COLORS.length
            ];
        },
        formatMinutes(value) {
            const totalMinutes = Math.max(0, Number(value) || 0);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            if(hours && minutes) {
                return `${hours}h ${minutes}m`;
            }
            if(hours) {
                return `${hours}h`;
            }
            return `${minutes}m`;
        },
        async handleBillingCodes() {
            let billingCodes = [];
            for (let i = 0; i < this.billingCodes.length; i++) {
                let billingCode = this.billingCodes[i];
                let defaultAmount = await this.getBillingAmount(billingCode.code);
                billingCodes.push({
                    code: billingCode.code,
                    quantity: 1,
                    minutes: billingCode.minutes,
                    amount: defaultAmount,
                });
            }
            this.$emit('billing-codes', billingCodes);
        },
    },
};
</script>
<style>
.billing-code-table .v-data-table__wrapper {
    overflow-x: hidden;
}

.billing-code-table table {
    table-layout: fixed;
    width: 100%;
}

.billing-code-table table th,
.billing-code-table table td {
    min-width: 0 !important;
    overflow: hidden;
    padding: 0 6px !important;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.billing-code-table table th:nth-last-child(2),
.billing-code-table table td:nth-last-child(2) {
    width: 42%;
}

.billing-code-table table th:last-child,
.billing-code-table table td:last-child {
    padding-left: 2px !important;
    padding-right: 2px !important;
    width: 38px;
}

.billing-allocation-slider {
    min-width: 180px;
}

.billing-allocation-value {
    display: inline-block;
    min-width: 52px;
    text-align: right;
}

.segmented-allocation {
    margin: 8px 0 16px;
}

.segmented-allocation__track {
    border-radius: 4px;
    display: flex;
    height: 16px;
    overflow: visible;
    position: relative;
}

.segmented-allocation__segment:first-child {
    border-radius: 4px 0 0 4px;
}

.segmented-allocation__segment:last-of-type {
    border-radius: 0 4px 4px 0;
}

.segmented-allocation__boundary {
    appearance: none;
    background: transparent;
    height: 16px;
    left: 0;
    margin: 0;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
}

.segmented-allocation__boundary::-webkit-slider-thumb {
    appearance: none;
    background: var(--v-primary-base);
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .45);
    cursor: ew-resize;
    height: 22px;
    pointer-events: auto;
    width: 22px;
}

.segmented-allocation__boundary::-moz-range-thumb {
    background: var(--v-primary-base);
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .45);
    cursor: ew-resize;
    height: 18px;
    pointer-events: auto;
    width: 18px;
}

.segmented-allocation__legend {
    display: grid;
    gap: 8px 16px;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    margin-top: 18px;
}

.segmented-allocation__legend-item {
    align-items: center;
    display: grid;
    gap: 7px;
    grid-template-columns: 10px 1fr auto;
}

.segmented-allocation__swatch {
    border-radius: 2px;
    height: 10px;
    width: 10px;
}
</style>
